import { useState, useEffect, useCallback, useMemo } from 'react';
import { TaglineService } from '../services/taglineService';

export type DisplayMode = 'welcome' | 'icebreaker';

const FIRST_VISIT_KEY = 'ionoff-first-visit-complete';
const TAGLINE_INDEX_KEY = 'ionoff-tagline-index';

interface UseWelcomesOptions {
  dismissedIcebreakers?: string[]; // List of exact icebreaker texts to hide
  onDismissIcebreaker?: (icebreaker: string) => void; // Callback when user dismisses an icebreaker
}

export const useWelcomes = (options: UseWelcomesOptions = {}) => {
  const { dismissedIcebreakers = [], onDismissIcebreaker } = options;
  
  const [welcomes, setWelcomes] = useState<string[]>([]);
  const [taglines, setTaglines] = useState<string[]>([]);
  const [allIcebreakers, setAllIcebreakers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Track if this is the user's very first visit to the site
  const [isFirstEverVisit, setIsFirstEverVisit] = useState<boolean>(() => {
    try {
      return localStorage.getItem(FIRST_VISIT_KEY) !== 'true';
    } catch {
      return true; // Assume first visit if localStorage unavailable
    }
  });
  
  // Track current display mode and index
  const [displayMode, setDisplayMode] = useState<DisplayMode>('welcome');
  const [currentIndex, setCurrentIndex] = useState<number>(() => {
    // Load persisted tagline index on mount (only for welcome mode)
    try {
      const saved = localStorage.getItem(TAGLINE_INDEX_KEY);
      if (saved) {
        const index = parseInt(saved, 10);
        if (!isNaN(index) && index >= 0) {
          return index;
        }
      }
    } catch {
      // Ignore localStorage errors
    }
    return 0;
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🚀 Fetching welcomes, taglines, and icebreakers from Firestore...');
      const [fetchedWelcomes, fetchedTaglines, fetchedIcebreakers] = await Promise.all([
        TaglineService.getWelcomes(),
        TaglineService.getTaglines(),
        TaglineService.getIcebreakers()
      ]);
      
      setWelcomes(fetchedWelcomes);
      setTaglines(fetchedTaglines);
      setAllIcebreakers(fetchedIcebreakers);
      console.log(`✅ Loaded ${fetchedWelcomes.length} welcomes, ${fetchedTaglines.length} taglines, and ${fetchedIcebreakers.length} icebreakers`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch welcomes/taglines/icebreakers';
      setError(errorMessage);
      console.error('❌ Fetching failed:', errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter icebreakers to exclude dismissed ones (exact match)
  const icebreakers = useMemo(() => {
    return allIcebreakers.filter(icebreaker => !dismissedIcebreakers.includes(icebreaker));
  }, [allIcebreakers, dismissedIcebreakers]);

  // Get the "home base" welcome message based on first visit status
  // First ever visit: welcomes[0] ("welcome to ionoff")
  // Subsequent visits: welcomes[1] ("ionoff" without "welcome to")
  const homeBaseWelcome = useMemo(() => {
    if (welcomes.length === 0) return 'better than any lone ai chatbot: welcome to ionoff';
    return isFirstEverVisit ? welcomes[0] : (welcomes[1] || welcomes[0]);
  }, [welcomes, isFirstEverVisit]);

  // Combined navigation list for welcome mode: home base + taglines
  // Index 0 is always the home base, followed by taglines
  const welcomeNavigationList = useMemo(() => {
    return [homeBaseWelcome, ...taglines];
  }, [homeBaseWelcome, taglines]);

  // Persist tagline index when navigating in welcome mode
  useEffect(() => {
    if (displayMode === 'welcome') {
      try {
        localStorage.setItem(TAGLINE_INDEX_KEY, String(currentIndex));
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [currentIndex, displayMode]);

  // Validate persisted index after data loads - reset if out of bounds
  // Only validate if taglines have actually loaded (length > 0)
  useEffect(() => {
    if (taglines.length > 0 && welcomeNavigationList.length > 0 && currentIndex >= welcomeNavigationList.length) {
      setCurrentIndex(0);
    }
  }, [taglines.length, welcomeNavigationList.length, currentIndex]);

  // Get current list based on display mode
  const currentList = displayMode === 'welcome' ? welcomeNavigationList : icebreakers;
  
  // Get current message to display
  const currentMessage = currentList[currentIndex] || (displayMode === 'welcome' 
    ? 'better than any lone ai chatbot: welcome to ionoff' 
    : "what's a question you wish people asked you more often?");

  // Switch to icebreakers list (called when "help me start" is clicked)
  const switchToIcebreakers = useCallback(() => {
    setDisplayMode('icebreaker');
    setCurrentIndex(0); // Go to first icebreaker
  }, []);

  // Toggle between welcomes and icebreakers (for "help me start" button)
  // If all icebreakers dismissed, stay on welcomes
  const toggleHelpMeStart = useCallback(() => {
    setDisplayMode(prev => {
      if (prev === 'welcome') {
        // Only switch to icebreakers if there are any available
        if (icebreakers.length > 0) {
          return 'icebreaker';
        }
        return 'welcome'; // Stay on welcomes if no icebreakers available
      }
      return 'welcome';
    });
    setCurrentIndex(0); // Always go to first item when toggling
  }, [icebreakers.length]);

  // Mark first visit as complete (called when user navigates or sends a message)
  const markFirstVisitComplete = useCallback(() => {
    if (isFirstEverVisit) {
      try {
        localStorage.setItem(FIRST_VISIT_KEY, 'true');
        setIsFirstEverVisit(false);
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [isFirstEverVisit]);

  // Navigate to previous item
  // For icebreakers: stay within icebreakers list (no wrap)
  // For welcome mode: go back through taglines but stop at home base (index 0)
  const goToPrevious = useCallback(() => {
    if (displayMode === 'icebreaker') {
      // Icebreakers: no wrap, just go back if possible
      setCurrentIndex(prev => (prev > 0 ? prev - 1 : prev));
    } else {
      // Welcome mode: go back but stop at home base (index 0)
      setCurrentIndex(prev => (prev > 0 ? prev - 1 : prev));
    }
  }, [displayMode]);

  // Navigate to next item
  // For icebreakers: stay within icebreakers list (no wrap)
  // For welcome mode: go through taglines, wrap back to home base at end
  const goToNext = useCallback(() => {
    // Mark first visit complete when user navigates
    markFirstVisitComplete();
    
    if (displayMode === 'icebreaker') {
      // Icebreakers: no wrap, just go forward if possible
      setCurrentIndex(prev => (prev < icebreakers.length - 1 ? prev + 1 : prev));
    } else {
      // Welcome mode: go forward, wrap back to home base at end
      setCurrentIndex(prev => {
        if (prev < welcomeNavigationList.length - 1) return prev + 1;
        // At end of taglines, wrap back to home base (index 0)
        return 0;
      });
    }
  }, [displayMode, icebreakers.length, welcomeNavigationList.length, markFirstVisitComplete]);

  // Dismiss the current icebreaker
  const dismissCurrentIcebreaker = useCallback(() => {
    if (displayMode !== 'icebreaker' || !currentMessage) return;
    
    // Call the callback to persist the dismissal
    onDismissIcebreaker?.(currentMessage);
    
    // After dismiss, navigate appropriately:
    // - If there are more icebreakers after filtering, stay on icebreakers
    // - If this was the last one, switch to welcomes
    const remainingAfterDismiss = icebreakers.length - 1;
    
    if (remainingAfterDismiss <= 0) {
      // No more icebreakers, switch to welcomes
      setDisplayMode('welcome');
      setCurrentIndex(0);
    } else {
      // Adjust index if we're at or beyond the new length
      setCurrentIndex(prev => {
        if (prev >= remainingAfterDismiss) {
          return remainingAfterDismiss - 1;
        }
        return prev;
      });
    }
  }, [displayMode, currentMessage, onDismissIcebreaker, icebreakers.length]);

  // Reset to welcome mode and home base (for new conversation)
  const reset = useCallback(() => {
    setDisplayMode('welcome');
    setCurrentIndex(0); // Home base is always index 0 in the navigation list
  }, []);

  // Called after first message is sent - marks first visit complete
  // and ensures we're showing the appropriate welcome (now handled automatically)
  const switchToSecondWelcome = useCallback(() => {
    markFirstVisitComplete();
    setDisplayMode('welcome');
    setCurrentIndex(0); // Home base
  }, [markFirstVisitComplete]);

  // Determine if we're at the boundary for showing navigation arrows
  // For icebreakers: no wrap, so hide arrows at boundaries
  // For welcome mode: no left arrow at home base (index 0), right arrow always visible (wraps)
  const canGoPrevious = displayMode === 'icebreaker' 
    ? currentIndex > 0 
    : currentIndex > 0; // No left arrow at home base (index 0)
  const canGoNext = displayMode === 'icebreaker' 
    ? currentIndex < icebreakers.length - 1 
    : true; // Always can go next in welcome mode (wraps back to home)

  return {
    welcomes,
    taglines,
    icebreakers,
    allIcebreakersCount: allIcebreakers.length,
    loading,
    error,
    refetch: fetchData,
    // Display state
    displayMode,
    currentIndex,
    currentMessage,
    currentListLength: currentList.length,
    isFirstEverVisit, // Whether this is the user's first ever visit
    // Navigation
    switchToIcebreakers,
    toggleHelpMeStart,
    goToPrevious,
    goToNext,
    canGoPrevious, // Whether to show left arrow
    canGoNext,     // Whether to show right arrow
    dismissCurrentIcebreaker,
    reset,
    switchToSecondWelcome,
    markFirstVisitComplete // Call when user sends first message
  };
};

